import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useAuth } from "@/App";
import { bookingAPI, statsAPI } from "@/lib/api";
import api from "@/lib/api";
import { toast } from "sonner";
import { 
  Package, 
  BookOpen,
  Search,
  Plane,
  Loader2,
  ArrowRight,
  Truck,
  Wallet,
  Plus,
  CreditCard
} from "lucide-react";

// Load Razorpay script
const loadRazorpay = () => {
  return new Promise((resolve) => {
    if (window.Razorpay) {
      resolve(true);
      return;
    }
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
};

export default function DashboardOverview() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [recentBookings, setRecentBookings] = useState([]);
  const [walletBalance, setWalletBalance] = useState(0);
  const [loading, setLoading] = useState(true);
  
  // Top-up state
  const [showTopUp, setShowTopUp] = useState(false);
  const [topUpAmount, setTopUpAmount] = useState("");
  const [processingTopUp, setProcessingTopUp] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [statsRes, bookingsRes, walletRes] = await Promise.all([
        statsAPI.getDashboard(),
        bookingAPI.getMyBookings(),
        api.get("/wallet/balance").catch(() => ({ data: { wallet_balance: 0 } }))
      ]);
      setStats(statsRes.data);
      setRecentBookings(bookingsRes.data.slice(0, 3));
      setWalletBalance(walletRes.data.wallet_balance || 0);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('Failed to fetch dashboard data:', error.message);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleTopUp = async () => {
    const amount = parseFloat(topUpAmount);
    if (isNaN(amount) || amount < 100) {
      toast.error("Minimum top-up amount is ₹100");
      return;
    }
    if (amount > 10000) {
      toast.error("Maximum top-up amount is ₹10,000");
      return;
    }

    setProcessingTopUp(true);
    try {
      // Load Razorpay SDK first
      const loaded = await loadRazorpay();
      
      const response = await api.post("/wallet/create-topup", { amount });
      const { topup_id, order_id, key_id } = response.data;
      
      if (!loaded || !window.Razorpay) {
        // Mock payment fallback
        const verifyRes = await api.post("/wallet/verify-topup", {
          topup_id: topup_id,
          razorpay_payment_id: `pay_mock_${Date.now()}`,
          razorpay_order_id: order_id,
          razorpay_signature: "mock_signature"
        });
        setWalletBalance(verifyRes.data.new_balance);
        toast.success(verifyRes.data.message + " (Test Mode)");
        setShowTopUp(false);
        setTopUpAmount("");
        return;
      }
      
      const options = {
        key: key_id,
        amount: amount * 100,
        currency: "INR",
        name: "Setuxpress",
        description: "Wallet Top-up",
        order_id: order_id,
        handler: async function (paymentResponse) {
          try {
            const verifyRes = await api.post("/wallet/verify-topup", {
              topup_id: topup_id,
              razorpay_payment_id: paymentResponse.razorpay_payment_id,
              razorpay_order_id: paymentResponse.razorpay_order_id,
              razorpay_signature: paymentResponse.razorpay_signature
            });
            
            setWalletBalance(verifyRes.data.new_balance);
            toast.success(verifyRes.data.message);
            setShowTopUp(false);
            setTopUpAmount("");
          } catch (error) {
            toast.error("Payment verification failed");
          }
        },
        prefill: {
          name: user?.name,
          email: user?.email
        },
        theme: {
          color: "#f97316"
        }
      };
      
      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create top-up order");
    } finally {
      setProcessingTopUp(false);
    }
  };

  const quickAmounts = [100, 200, 500, 1000];

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl font-black tracking-tight mb-2">
          Welcome, {user?.name}
        </h1>
        <p className="text-muted-foreground">
          Your shipping dashboard
        </p>
      </div>

      {loading ? (
        <div className="flex justify-center py-10">
          <Loader2 className="w-6 h-6 animate-spin" />
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-border border border-border">
            <div className="bg-background p-6">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 bg-emerald-500 flex items-center justify-center">
                  <BookOpen className="w-5 h-5 text-white" />
                </div>
                <span className="label-text">My Bookings</span>
              </div>
              <p className="text-4xl font-black">{stats?.total_bookings || 0}</p>
            </div>
            <div className="bg-background p-6">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-orange-500 flex items-center justify-center">
                    <Wallet className="w-5 h-5 text-white" />
                  </div>
                  <span className="label-text">Wallet Balance</span>
                </div>
                <Button 
                  size="sm" 
                  variant="outline" 
                  className="rounded-none border-orange-500 text-orange-600 hover:bg-orange-50"
                  onClick={() => setShowTopUp(true)}
                  data-testid="wallet-topup-btn"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Top Up
                </Button>
              </div>
              <p className="text-4xl font-black">₹{walletBalance.toFixed(2)}</p>
              <p className="text-xs text-muted-foreground mt-1">Use for faster checkouts</p>
            </div>
            <div className="bg-background p-6 flex items-center justify-center">
              <Link to="/browse">
                <Button className="rounded-none btn-hover" data-testid="browse-space-btn">
                  <Search className="w-4 h-4 mr-2" />
                  Browse Cargo Space
                </Button>
              </Link>
            </div>
          </div>

          {/* Wallet Top-up Dialog */}
          <Dialog open={showTopUp} onOpenChange={setShowTopUp}>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Wallet className="w-5 h-5 text-orange-500" />
                  Top Up Wallet
                </DialogTitle>
                <DialogDescription>
                  Add money to your wallet for faster checkouts. Current balance: ₹{walletBalance.toFixed(2)}
                </DialogDescription>
              </DialogHeader>
              
              <div className="space-y-4 pt-4">
                <div className="grid grid-cols-4 gap-2">
                  {quickAmounts.map(amt => (
                    <Button
                      key={amt}
                      variant={topUpAmount === String(amt) ? "default" : "outline"}
                      className="rounded-none"
                      onClick={() => setTopUpAmount(String(amt))}
                      data-testid={`topup-quick-${amt}`}
                    >
                      ₹{amt}
                    </Button>
                  ))}
                </div>
                
                <div className="space-y-2">
                  <label className="text-sm font-medium">Or enter custom amount</label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">₹</span>
                    <Input
                      type="number"
                      placeholder="Enter amount"
                      value={topUpAmount}
                      onChange={(e) => setTopUpAmount(e.target.value)}
                      className="pl-8 rounded-none"
                      min="100"
                      max="10000"
                      data-testid="topup-amount-input"
                    />
                  </div>
                  <p className="text-xs text-muted-foreground">Min: ₹100 | Max: ₹10,000</p>
                </div>
                
                {topUpAmount && parseFloat(topUpAmount) >= 100 && (
                  <div className="bg-orange-50 border border-orange-200 p-3 space-y-1">
                    <div className="flex justify-between text-sm">
                      <span>Top-up amount</span>
                      <span className="font-medium">₹{parseFloat(topUpAmount).toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-sm font-bold text-orange-600">
                      <span>New balance</span>
                      <span>₹{(walletBalance + parseFloat(topUpAmount)).toFixed(2)}</span>
                    </div>
                  </div>
                )}
                
                <Button
                  className="w-full rounded-none bg-orange-500 hover:bg-orange-600"
                  onClick={handleTopUp}
                  disabled={processingTopUp || !topUpAmount || parseFloat(topUpAmount) < 100}
                  data-testid="topup-pay-btn"
                >
                  {processingTopUp ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <CreditCard className="w-4 h-4 mr-2" />
                      Pay ₹{topUpAmount || 0}
                    </>
                  )}
                </Button>
                
                <p className="text-xs text-center text-muted-foreground">
                  Secured by Razorpay. Money will be added instantly.
                </p>
              </div>
            </DialogContent>
          </Dialog>

          {recentBookings.length > 0 && (
            <div className="space-y-4">
              <h2 className="text-xl font-bold">Recent Bookings</h2>
              {recentBookings.map((booking) => (
                <div key={booking.id} className="border border-border p-4 flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 bg-secondary flex items-center justify-center">
                      {booking.listing_type === 'jet' ? (
                        <Plane className="w-5 h-5" />
                      ) : (
                        <Package className="w-5 h-5" />
                      )}
                    </div>
                    <div>
                      <div className="flex items-center gap-2 font-medium">
                        <span>{booking.listing_details.origin_city}</span>
                        <ArrowRight className="w-4 h-4 text-muted-foreground" />
                        <span>{booking.listing_details.destination_city}</span>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        {booking.listing_details.departure_date} • {booking.quantity} {booking.listing_type === 'cargo' ? 'kg' : 'seats'}
                      </p>
                    </div>
                  </div>
                  <span className="px-2 py-1 text-xs font-bold uppercase status-active">
                    {booking.status}
                  </span>
                </div>
              ))}
              
              <Link to="/dashboard/bookings" className="block">
                <Button variant="outline" className="w-full rounded-none" data-testid="view-all-bookings-btn">
                  View All Bookings
                </Button>
              </Link>
            </div>
          )}

          <div className="grid md:grid-cols-2 gap-4">
            <Link to="/browse" className="border border-border p-6 hover:bg-secondary/50 transition-colors">
              <Truck className="w-8 h-8 mb-4 text-orange-500" />
              <h3 className="font-bold mb-1">Find Cargo Space</h3>
              <p className="text-sm text-muted-foreground">
                Browse available cargo space on road, rail, air, and water routes.
              </p>
            </Link>
            <Link to="/private-jets" className="border border-border p-6 hover:bg-secondary/50 transition-colors">
              <Plane className="w-8 h-8 mb-4 text-amber-500" />
              <h3 className="font-bold mb-1">Private Jets</h3>
              <p className="text-sm text-muted-foreground">
                Book luxury travel on private jets at shared costs.
              </p>
            </Link>
          </div>
        </>
      )}
    </div>
  );
}
